import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useToast } from './ToastContext';

const API_BASE = '/api';

interface User {
  id: string;
  name: string;
  email: string;
  job_role: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

function UsersPage() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (checked && !user) {
      navigate('/admin');
    }
  }, [checked, user, navigate]);

  const loadUsers = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/users`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!checked || !user) {
    return <div className="app"><p>Checking access...</p></div>;
  }

  const confirmDeactivate = (userId: string, name: string) => {
    setDeactivateConfirm({ id: userId, name });
  };

  const handleDeactivate = async () => {
    if (!deactivateConfirm) return;

    const res = await fetch(`${API_BASE}/admin/users/${deactivateConfirm.id}/deactivate`, {
      method: 'POST',
      credentials: 'include',
    });

    setDeactivateConfirm(null);

    if (res.ok) {
      showToast(`${deactivateConfirm.name} has been deactivated.`);
      loadUsers();
    } else {
      showToast('Failed to deactivate user.', 'error');
    }
  };

  const handleReactivate = async (userId: string, name: string) => {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/reactivate`, {
      method: 'POST',
      credentials: 'include',
    });

    if (res.ok) {
      showToast(`${name} has been reactivated.`);
      loadUsers();
    } else {
      showToast('Failed to reactivate user.', 'error');
    }
  };

  const handleResetPassword = async (userId: string, name: string) => {
    const res = await fetch(`${API_BASE}/admin/users/${userId}/reset-password`, {
      method: 'POST',
      credentials: 'include',
    });

    if (res.ok) {
      showToast(`Password reset link sent to ${name}.`);
    } else {
      showToast('Failed to send reset link.', 'error');
    }
  };

  return (
    <div className="app">
      <div className="top-bar">
        <Link to="/" className="back-button">&larr; Back to Categories</Link>
      </div>

      <div className="page-header-row">
        <h1>Users</h1>
        <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
          + Add User
        </button>
      </div>

      {loading && <p>Loading...</p>}

      <div className="doc-table-wrapper users-table-wrapper">
        <table className="doc-table users-table">
          <thead>
            <tr>
              <th className="col-name">Name</th>
              <th className="col-email">Email Address</th>
              <th className="col-modified">Created</th>
              <th className="col-status">Status</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td className="col-name">{u.name}</td>
                <td className="col-email">{u.email}</td>
                <td className="col-modified">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="col-status">
                  <span className={`status-badge ${u.is_active ? 'status-active' : 'status-inactive'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="col-actions">
                  <div className="action-icons">
                    {u.id === user.id ? (
                      <span className="text-button-disabled" title="Use the Forgot Password link on the login page instead">
                        Reset Password
                      </span>
                    ) : (
                      <button className="text-button" onClick={() => handleResetPassword(u.id, u.name)}>
                        Reset Password
                      </button>
                    )}
                    {u.is_active ? (
                      u.id === user.id ? (
                        <span className="text-button-disabled" title="You cannot deactivate your own account">
                          Deactivate
                        </span>
                      ) : (
                        <button className="text-button text-button-danger" onClick={() => confirmDeactivate(u.id, u.name)}>
                          Deactivate
                        </button>
                      )
                    ) : (
                      <button className="text-button" onClick={() => handleReactivate(u.id, u.name)}>
                        Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && !loading && (
              <tr><td colSpan={5}>No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            showToast('User invited successfully.');
            loadUsers();
          }}
        />
      )}
      {deactivateConfirm && (
        <div className="modal-overlay" onClick={() => setDeactivateConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-topleft" onClick={() => setDeactivateConfirm(null)} aria-label="Close">
              &times;
            </button>
            <h2>Deactivate User?</h2>
            <p>
              Deactivate <strong>{deactivateConfirm.name}</strong>? They will be logged out immediately and unable to access the admin panel until reactivated.
            </p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeactivateConfirm(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleDeactivate}>Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface InviteUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function InviteUserModal({ onClose, onSuccess }: InviteUserModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/admin/users/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to invite user');
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-topleft" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <h2>Add User</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              className="invite-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              className="invite-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {error && <p className="error">{error}</p>}

          <button type="submit" className="btn-primary btn-full-width" disabled={submitting}>
            {submitting ? 'Sending Invite...' : 'Send Invite'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UsersPage;